import { useMutation } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import AdotelSkeleton from '../../components/AdotelSkeleton'
import { RATE_CONNECTION_GENERATE_MUTATION } from '../../graphql/rateConnection'
import { CREATE_WALLETS_FOR_SUB_MUTATION } from '../../graphql/wallet'
import { DateObject, getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import {
  RadioButtonType,
  RateConnectionGenerateInput,
  SelectedRatePlans,
  StructuredData
} from '../../types/rateConnection'
import { EntityInfo, WalletsForSubInput } from '../../types/wallet'
import useStyles from './styles/AccessButtonSection.styles'

interface AccessButtonSectionProps {
  input: RateConnectionGenerateInput
  setInput: React.Dispatch<React.SetStateAction<RateConnectionGenerateInput>>
  setAutoCompleteForPriceClass: React.Dispatch<any>
  setSelectedDates: React.Dispatch<React.SetStateAction<DateObject[]>>
  setSelectedWeekDaysArray: React.Dispatch<React.SetStateAction<string[][]>>
  autoCompleteForProvider: any
  setAutoCompleteForProvider: React.Dispatch<any>
  structuredDataAccommodation: StructuredData[]
  setStructuredDataAccommodation: React.Dispatch<
    React.SetStateAction<StructuredData[]>
  >
  structuredDataChannel: StructuredData[]
  setStructuredDataChannel: React.Dispatch<
    React.SetStateAction<StructuredData[]>
  >
  radioButtonValue: RadioButtonType
  loadingToGetData: boolean
}

export default function AccessButtonSection({
  input,
  setInput,
  setAutoCompleteForPriceClass,
  setSelectedDates,
  setSelectedWeekDaysArray,
  autoCompleteForProvider,
  setAutoCompleteForProvider,
  structuredDataAccommodation,
  setStructuredDataAccommodation,
  structuredDataChannel,
  setStructuredDataChannel,
  radioButtonValue,
  loadingToGetData
}: AccessButtonSectionProps) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const [walletsForSubInput, setWalletsForSubInput] =
    useState<WalletsForSubInput>({
      channelsInfo: [],
      providerInfo: {
        entityCode: '',
        id: ''
      }
    })

  const [createWallets] = useMutation(CREATE_WALLETS_FOR_SUB_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onError: (err) => toast.error(err.message)
  })

  const [connectRates, { loading: loadingForSubORUnsub }] = useMutation(
    RATE_CONNECTION_GENERATE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp, { context }: any) => {
        const newJob: JobsType = {
          trackID: resp.rateConnectionGenerate.trackingID,
          creationDate: getTodayDate(),
          status: 'InProgress'
        }
        addJob(newJob)
        toast.info(
          `Rate Connection process with TrackingID ${resp.rateConnectionGenerate.trackingID} started`
        )

        if (context?.sub === 'Subscribe') {
          createWallets({
            variables: {
              input: walletsForSubInput
            }
          })
        }
      },
      onError: (err) => toast.error(err.message)
    }
  )

  const handleClickOnAccess = (sub: string) => {
    const selectedRatePlans: SelectedRatePlans[] = []

    structuredDataAccommodation.forEach((accNode) => {
      if (accNode.checked || accNode.indeterminate) {
        const propertyCode = accNode.code
        const displayPropertyCode = accNode.displayCode

        accNode.children.forEach((roomTypeNode) => {
          if (roomTypeNode.checked || roomTypeNode.indeterminate) {
            const roomTypeCode = roomTypeNode.code
            const displayRoomTypeCode = roomTypeNode.displayCode
            roomTypeNode.children.forEach((ratePlanNode) => {
              if (ratePlanNode.checked) {
                const ratePlanCode = ratePlanNode.code
                const displayRatePlanCode = ratePlanNode.displayCode

                selectedRatePlans.push({
                  propertyCode,
                  roomTypeCode,
                  ratePlanCode,
                  displayPropertyCode,
                  displayRatePlanCode,
                  displayRoomTypeCode
                })
              }
            })
          }
        })
      }
    })

    const selectedChannelsCode: string[] = []
    structuredDataChannel.forEach((ch) => {
      if (ch.checked) {
        const channelCode = ch.code
        selectedChannelsCode.push(channelCode)
      }
    })

    const chnlsInfo: EntityInfo[] = structuredDataChannel
      .filter((item) => item.checked)
      .map((ele) => {
        return {
          entityCode: ele.code,
          id: ele.id
        }
      })
    setWalletsForSubInput({
      ...walletsForSubInput,
      channelsInfo: chnlsInfo,
      providerInfo: {
        entityCode: autoCompleteForProvider && autoCompleteForProvider.code,
        id: autoCompleteForProvider && autoCompleteForProvider.value
      },
      priceClassID: input.priceClassID ? input.priceClassID : undefined,
      currency: input.priceClassID ? undefined : input.inputCurrency
    })

    const cleanedSelectedRatePlans = selectedRatePlans.map((rp) =>
      Object.fromEntries(
        Object.entries(rp).filter(
          ([key]) => !key.toLowerCase().startsWith('display')
        )
      )
    )

    connectRates({
      variables: {
        input: {
          date: input.date,
          priceClassID: input.priceClassID ? input.priceClassID : undefined,
          rateMultiplier: input.rateMultiplier
            ? input.rateMultiplier
            : undefined,
          providerCode: input.providerCode,
          selectedRatePlans: cleanedSelectedRatePlans,
          selectedChannelsCode: selectedChannelsCode,
          sub: sub === 'Subscribe',
          inputCurrency: input.inputCurrency,
          outputCurrency: input.outputCurrency
        }
      },
      context: { sub }
    })
  }

  const handleClickOnReset = () => {
    setInput({
      inputCurrency: '',
      outputCurrency: '',
      priceClassID: '',
      date: [],
      providerCode: '',
      selectedRatePlans: [],
      selectedChannelsCode: []
    })
    setAutoCompleteForPriceClass(null)
    setSelectedDates([{}])
    setSelectedWeekDaysArray([[]])
    setAutoCompleteForProvider(null)
    setStructuredDataAccommodation([]) //to delete all accommodations

    const updatedChannelData: StructuredData[] = []
    structuredDataChannel.forEach((ch) => {
      updatedChannelData.push({ ...ch, checked: false })
    })
    setStructuredDataChannel(updatedChannelData)
  }

  const disableSubscribeButton =
    loadingForSubORUnsub ||
    (radioButtonValue === 'PriceClass' && !input.priceClassID) ||
    (radioButtonValue === 'RateMultiplier' && !input.rateMultiplier) ||
    !input.providerCode ||
    loadingToGetData

  const disableUnSubscribeButton =
    loadingForSubORUnsub ||
    (radioButtonValue === 'PriceClass' && !input.priceClassID) ||
    (radioButtonValue === 'RateMultiplier' && !input.rateMultiplier) ||
    !input.providerCode ||
    loadingToGetData

  return (
    <div className={classes.root}>
      {!isMobileScreen && <div className={classes.line} />}
      <Button
        className={classes.accessButton}
        onClick={() => handleClickOnAccess('Subscribe')}
        disabled={disableSubscribeButton}>
        <Typography className={classes.buttonText}>
          <LinkIcon />
          {getTranslate('Subscribe')}
        </Typography>
      </Button>
      <Button className={classes.resetButton} onClick={handleClickOnReset}>
        <Typography className={classes.buttonTextSmall}>
          <SettingsBackupRestoreIcon />
          {getTranslate('Reset')}
        </Typography>
      </Button>
      <Button
        className={classes.accessButton}
        onClick={() => handleClickOnAccess('Unsubscribe')}
        disabled={disableUnSubscribeButton}>
        <Typography className={classes.buttonText}>
          <LinkOffIcon />
          {getTranslate('Unsubscribe')}
        </Typography>
      </Button>
      {!isMobileScreen && (
        <div className={[classes.line, classes.shortLine].join(' ')} />
      )}
      <AdotelSkeleton
        open={loadingForSubORUnsub}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
    </div>
  )
}
